import { useEffect, useState } from 'react';
import capitalize from 'lodash/capitalize';
import { useTranslation } from 'react-i18next';

import { IConvertSize } from 'shared/types/ConvertSize';
import { ISize } from 'shared/types/Size';
import { requestService } from 'shared/utils/services/requestService';
import { useAuthState } from 'auth/hooks/useAuth';
import { useProductTemplate } from 'productTemplate/hooks/useProductTemplate';
import { useGenderLabel } from './useGenderLabel';

export const useGenderSizingLabel = (size: ISize) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const { isFashionProduct, productType, singleGender, sizeBrand, sizeUnit } = useProductTemplate();
  const getGenderLabel = useGenderLabel();
  const defaultTitle = `${sizeUnit.toUpperCase()} ${String(
    capitalize(getGenderLabel(singleGender)),
  )} ${String(t('productTemplate.size', 'Size'))} ${size?.presentation?.toUpperCase()}`;
  const [genderSizeLabel, setGenderSizeLabel] = useState(defaultTitle);
  const [isSizeConverted, setIsSizeConverted] = useState(false);
  const { user } = useAuthState();
  const isApparel = isFashionProduct || (productType && productType !== 'sneakers');
  const {
    sizeBrand: userSizeBrand,
    sizeGender: userSizeGender,
    convertedSizeUnit: userSizeUnit,
  } = user || {};
  useEffect(() => {
    const getConvertedSize = async () => {
      try {
        setIsLoading(true);
        const { data: convertedSize }: { data: IConvertSize } = await requestService({
          url: '/web-api/v1/convert_size',
          method: 'post',
          data: JSON.stringify({
            size: String(size.value),
            from_brand: sizeBrand,
            to_brand: sizeBrand || 'default',
            from_gender: singleGender?.toUpperCase(),
            to_gender: userSizeGender?.toUpperCase(),
            from_unit: sizeUnit?.toUpperCase(),
            to_unit: userSizeUnit?.toUpperCase(),
            product_type: 'SNEAKERS',
          }),
        });

        if (
          convertedSize?.unit?.toLowerCase() !== sizeUnit?.toLowerCase() ||
          convertedSize?.gender?.toLowerCase() !== singleGender?.toLowerCase() ||
          convertedSize?.size !== String(size.value)
        ) {
          const userTitle = `${convertedSize?.unit?.toUpperCase()} ${String(
            capitalize(getGenderLabel(convertedSize?.gender)),
          )} ${t('productTemplate.size', 'Size')} ${convertedSize?.size}`;

          setIsSizeConverted(true);
          setGenderSizeLabel(`${defaultTitle} / ${userTitle}`);
        }
      } catch {
        setGenderSizeLabel(defaultTitle);
      } finally {
        setIsLoading(false);
      }
    };

    if (
      !isApparel &&
      userSizeGender &&
      userSizeUnit &&
      (sizeBrand?.toLowerCase() !== userSizeBrand?.toLowerCase() ||
        singleGender?.toLowerCase() !== userSizeGender?.toLowerCase() ||
        sizeUnit?.toLowerCase() !== userSizeUnit?.toLowerCase())
    ) {
      void getConvertedSize();
    }
    return () => {
      setIsLoading(false);
    };
  }, [isApparel, size, userSizeBrand, userSizeGender, userSizeUnit]);

  return {
    isLoading,
    isSizeConverted,
    genderSizeLabel,
  };
};
